import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp, writeBatch, query, where, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "./config";
import { Expense } from "../types/Expense";
import { User } from "firebase/auth";
import { generateRandomColor } from '../utils/colorUtils';
import { Income } from "../types/Income";
import { Investment } from "../types/Investment";
import { BankAccount } from "../types/BankAccount";
import { FirebaseError } from "firebase/app";

const COLLECTION_NAME = "expenses";
const INCOME_COLLECTION = "income";

export const expensesCache = new Map<string, Expense[]>();
export const incomeCache = new Map<string, Income[]>();

export const getPayers = async (): Promise<string[]> => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    const expensesCollection = collection(db, "expenses");
    const q = query(expensesCollection, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    const payers = new Set<string>();
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.payer) payers.add(data.payer);
      if (data.sharedWith) {
        // Check if sharedWith is an array
        if (Array.isArray(data.sharedWith)) {
          data.sharedWith.forEach(payer => payers.add(payer));
        } else {
          payers.add(data.sharedWith);
        }
      }
    });
    return Array.from(payers);
  } catch (error) {
    console.error("Error getting payers:", error);
    return [];
  }
};

export const addExpense = async (expense: Omit<Expense, 'id'>, user: User, file?: File): Promise<Expense> => {
  try {
    let fileUrl = '';
    if (file) {
      const storageRef = ref(storage, `receipts/${user.uid}/${file.name}`);
      const uploadResult = await uploadBytes(storageRef, file);
      fileUrl = await getDownloadURL(storageRef);
    }

    const expenseWithTimestamp = {
      ...expense,
      userId: user.uid,
      payer: expense.payer || '',
      sharedWith: expense.sharedWith || '',
      date: Timestamp.fromDate(new Date(expense.date)),
      fileUrl,
      isDebt: expense.isDebt || false,
      debtFrom: expense.debtFrom || null,
      bankAccountId: expense.bankAccountId || null,
    };

    // If a bank account is selected, update its balance
    if (expense.bankAccountId) {
      const accountRef = doc(db, "bankAccounts", expense.bankAccountId);
      const accountDoc = await getDoc(accountRef);

      if (accountDoc.exists()) {
        const accountData = accountDoc.data() as BankAccount;
        const newBalance = accountData.balance - expense.amount;

        await updateDoc(accountRef, {
          balance: newBalance,
          updatedAt: new Date().toISOString()
        });
      }
    }

    const docRef = await addDoc(collection(db, COLLECTION_NAME), expenseWithTimestamp);
    const newExpense = {
      ...expense,
      id: docRef.id,
      date: expenseWithTimestamp.date.toDate().toISOString().split('T')[0],
      fileUrl
    };
    updateExpenseCache(user.uid, newExpense);
    return newExpense;
  } catch (error) {
    console.error("Error in addExpense function:", error);
    throw new Error(`Failed to add expense: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const getExpenses = async (user: User): Promise<Expense[]> => {
  try {
    const cacheKey = user.uid;
    if (expensesCache.has(cacheKey)) {
      return expensesCache.get(cacheKey)!;
    }

    const expensesCollection = collection(db, "expenses");
    const q = query(expensesCollection, where("userId", "==", user.uid));
    const querySnapshot = await getDocs(q);

    const expenses: Expense[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date.toDate().toISOString().split('T')[0]
    } as Expense));

    expensesCache.set(cacheKey, expenses);
    return expenses;
  } catch (error) {
    console.error("Error in getExpenses function:", error);
    throw new Error(`Failed to get expenses: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const updateExpense = async (expense: Expense, user: User): Promise<void> => {
  try {
    const expenseRef = doc(db, "expenses", expense.id);
    const expenseDoc = await getDoc(expenseRef);

    if (!expenseDoc.exists()) {
      throw new Error("Expense does not exist");
    }

    const expenseData = expenseDoc.data();
    if (expenseData.userId !== user.uid) {
      throw new Error("You do not have permission to update this expense");
    }

    const { id, ...updateData } = expense;

    await updateDoc(expenseRef, {
      ...updateData,
      payer: updateData.payer || '',
      sharedWith: updateData.sharedWith || '',
      date: Timestamp.fromDate(new Date(expense.date)),
      shared: expense.shared,
    });
    updateExpenseCache(user.uid, expense);
  } catch (error) {
    console.error("Error updating expense: ", error);
    throw new Error("Failed to update expense");
  }
};

export const deleteExpense = async (id: string, user: User): Promise<void> => {
  try {
    const expenseRef = doc(db, "expenses", id);
    const expenseDoc = await getDoc(expenseRef);

    if (expenseDoc.exists() && expenseDoc.data().userId === user.uid) {
      await deleteDoc(expenseRef);
      const expenses = expensesCache.get(user.uid) || [];
      expensesCache.set(user.uid, expenses.filter(e => e.id !== id));
    } else {
      throw new Error("You do not have permission to delete this expense");
    }
  } catch (error) {
    console.error("Error deleting expense: ", error);
    throw new Error("Failed to delete expense");
  }
};

export const updateCategory = async (oldCategory: string, newCategory: string): Promise<void> => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("User not authenticated");
  
  const batch = writeBatch(db);
  const expensesRef = collection(db, "expenses");
  const q = query(expensesRef, where("category", "==", oldCategory), where("userId", "==", userId));
  const querySnapshot = await getDocs(q);

  querySnapshot.forEach((doc) => {
    batch.update(doc.ref, { category: newCategory });
  });

  await batch.commit();
};

export const getPayerColors = async (): Promise<{ [key: string]: string }> => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) return {};
    const payerColorsDoc = await getDoc(doc(db, "payerColors", userId));
    if (payerColorsDoc.exists()) {
      const data = payerColorsDoc.data();
      return data as { [key: string]: string };
    } else {
      return {};
    }
  } catch (error) {
    console.error("Error fetching payer colors:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
    }
    // Instead of throwing, return an empty object
    return {};
  }
};

export const initializePayerColors = async () => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const payerColorsRef = doc(db, "payerColors", userId);
    const payerColorsDoc = await getDoc(payerColorsRef);

    if (!payerColorsDoc.exists()) {
      const initialColors = {
        "Bruno": "#64B5F6",
        "Simone": "#FFB74D",
        "Bárbara": "#81C784"
      };
      await setDoc(payerColorsRef, initialColors);
    } else {
      const existingColors = payerColorsDoc.data();
      const payers = await getPayers(); // Agora esta função está definida
      const updatedColors = { ...existingColors };
      let hasNewColors = false;

      payers.forEach((payer: string) => { // Adicionamos o tipo explicitamente aqui
        if (!updatedColors[payer]) {
          updatedColors[payer] = generateRandomColor();
          hasNewColors = true;
        }
      });

      if (hasNewColors) {
        await setDoc(payerColorsRef, updatedColors);
      }
    }
  } catch (error) {
    console.error("Error initializing payer colors:", error);
  }
};

export const updatePayerColor = async (payer: string, color: string): Promise<void> => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error("User not authenticated");
    const payerColorsRef = doc(db, "payerColors", userId);
    await setDoc(payerColorsRef, { [payer]: color }, { merge: true });
  } catch (error) {
    console.error("Error updating payer color:", error);
    throw new Error("Failed to update payer color");
  }
};

// Category Icons Operations
export const getCategoryIcons = async (): Promise<{ [key: string]: string }> => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) return {};
    const categoryIconsDoc = await getDoc(doc(db, "categoryIcons", userId));
    if (categoryIconsDoc.exists()) {
      const data = categoryIconsDoc.data();
      return data as { [key: string]: string };
    } else {
      return {};
    }
  } catch (error) {
    console.error("Error fetching category icons:", error);
    return {};
  }
};

export const updateCategoryIcon = async (category: string, iconName: string): Promise<void> => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error("User not authenticated");
    const categoryIconsRef = doc(db, "categoryIcons", userId);
    await setDoc(categoryIconsRef, { [category]: iconName }, { merge: true });
  } catch (error) {
    console.error("Error updating category icon:", error);
    throw new Error("Failed to update category icon");
  }
};

// Explicitly export types
export type { Expense };

// Add this line at the end of the file
export { };

export const deleteAllExpenses = async (user: User): Promise<void> => {
  try {
    const expensesRef = collection(db, "expenses");
    const q = query(expensesRef, where("userId", "==", user.uid));
    const querySnapshot = await getDocs(q);

    const batch = writeBatch(db);
    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    expensesCache.delete(user.uid);
  } catch (error) {
    console.error("Error deleting all expenses: ", error);
    throw new Error("Failed to delete all expenses");
  }
};

export const addIncome = async (income: Omit<Income, 'id' | 'userId'>, userId: string, file?: File): Promise<Income> => {
  try {
    const incomeWithTimestamp = {
      ...income,
      date: Timestamp.fromDate(new Date(income.date)),
      userId,
    };

    const docRef = await addDoc(collection(db, INCOME_COLLECTION), incomeWithTimestamp);

    const newIncome = {
      ...incomeWithTimestamp,
      id: docRef.id,
      date: incomeWithTimestamp.date.toDate().toISOString().split('T')[0],
    };
    updateIncomeCache(userId, newIncome);
    return newIncome;
  } catch (error) {
    console.error("Error in addIncome function:", error);
    throw new Error(`Failed to add income: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const getIncome = async (user: User): Promise<Income[]> => {
  try {
    const cacheKey = user.uid;
    if (incomeCache.has(cacheKey)) {
      return incomeCache.get(cacheKey)!;
    }

    const incomeRef = collection(db, INCOME_COLLECTION);
    const incomeQuery = query(incomeRef, where("userId", "==", user.uid));
    const querySnapshot = await getDocs(incomeQuery);

    const incomeData = querySnapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id,
      date: doc.data().date instanceof Timestamp ? doc.data().date.toDate().toISOString().split('T')[0] : doc.data().date,
    } as Income));

    incomeCache.set(cacheKey, incomeData);
    return incomeData;
  } catch (error) {
    console.error("Error fetching income:", error);
    throw new Error(`Failed to fetch income: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const updateIncome = async (income: Income, user: User): Promise<void> => {
  try {
    const incomeRef = doc(db, INCOME_COLLECTION, income.id);
    const incomeDoc = await getDoc(incomeRef);

    if (!incomeDoc.exists()) {
      throw new Error("Income entry does not exist");
    }

    const incomeData = incomeDoc.data();
    if (incomeData.userId !== user.uid) {
      throw new Error("You do not have permission to update this income entry");
    }

    const { id, ...updateData } = income;

    await updateDoc(incomeRef, {
      ...updateData,
      date: Timestamp.fromDate(new Date(income.date)),
    });
    updateIncomeCache(user.uid, income);
  } catch (error) {
    console.error("Error updating income entry: ", error);
    throw new Error("Failed to update income entry");
  }
};

export const deleteIncome = async (id: string, user: User): Promise<void> => {
  try {
    const incomeRef = doc(db, INCOME_COLLECTION, id);
    const incomeDoc = await getDoc(incomeRef);

    if (incomeDoc.exists() && incomeDoc.data().userId === user.uid) {
      await deleteDoc(incomeRef);
      const incomes = incomeCache.get(user.uid) || [];
      incomeCache.set(user.uid, incomes.filter(i => i.id !== id));
    } else {
      throw new Error("You do not have permission to delete this income entry");
    }
  } catch (error) {
    console.error("Error deleting income entry: ", error);
    throw new Error("Failed to delete income entry");
  }
};

export const updateIncomeCategory = async (oldCategory: string, newCategory: string, user: User): Promise<void> => {
  const incomeRef = collection(db, 'income');
  const q = query(incomeRef, where('userId', '==', user.uid), where('category', '==', oldCategory));
  const querySnapshot = await getDocs(q);

  const batch = writeBatch(db);
  querySnapshot.forEach((doc) => {
    batch.update(doc.ref, { category: newCategory });
  });

  await batch.commit();
};

// Add investment
export const addInvestment = async (userId: string, investment: Omit<Investment, 'id'>): Promise<void> => {
  try {

    const investmentsRef = collection(db, 'investments');
    const newInvestmentRef = doc(investmentsRef);
    await setDoc(newInvestmentRef, {
      ...investment,
      id: newInvestmentRef.id,
      userId: userId,
    });
  } catch (error) {
    console.error("Error adding investment: ", error);
    if (error instanceof FirebaseError) {
      console.error("Firebase error code:", error.code);
      console.error("Firebase error message:", error.message);
      if (error.code === 'permission-denied') {
        throw new Error("You don't have permission to add investments. Please check your account permissions.");
      }
    }
    throw error;
  }
};

// Get investments for a user
export const getInvestments = async (userId: string) => {
  try {
    const q = query(collection(db, 'investments'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        purchaseDate: data.purchaseDate instanceof Timestamp ? data.purchaseDate.toDate() : data.purchaseDate,
      } as Investment;
    });
  } catch (error) {
    console.error('Error getting investments: ', error);
    throw error;
  }
};

// Update investment
export const updateInvestment = async (id: string, investment: Partial<Investment>, userId: string) => {
  try {
    const investmentRef = doc(db, 'investments', id);
    const investmentDoc = await getDoc(investmentRef);
    if (!investmentDoc.exists() || investmentDoc.data().userId !== userId) {
      throw new Error("You don't have permission to update this investment.");
    }

    const { id: _, userId: __, ...updateDataRaw } = investment;
    const updateData: any = { ...updateDataRaw };

    if (investment.purchaseDate) {
      // Ensure purchaseDate is a valid Date before converting
      const date = investment.purchaseDate instanceof Date
        ? investment.purchaseDate
        : (investment.purchaseDate as any).toDate
          ? (investment.purchaseDate as any).toDate()
          : new Date(investment.purchaseDate as any);

      if (!isNaN(date.getTime())) {
        updateData.purchaseDate = Timestamp.fromDate(date);
      }
    } else {
      updateData.purchaseDate = null;
    }

    await updateDoc(investmentRef, updateData);
  } catch (error) {
    console.error('Error updating investment: ', error);
    throw error;
  }
};

// Delete investment
export const deleteInvestment = async (id: string, userId: string) => {
  try {
    const investmentRef = doc(db, 'investments', id);
    const investmentDoc = await getDoc(investmentRef);
    if (!investmentDoc.exists() || investmentDoc.data().userId !== userId) {
      throw new Error("You don't have permission to delete this investment.");
    }
    await deleteDoc(investmentRef);
  } catch (error) {
    console.error('Error deleting investment: ', error);
    throw error;
  }
};




export const updateExpenseCache = (userId: string, expense: Expense) => {
  const expenses = expensesCache.get(userId) || [];
  const index = expenses.findIndex(e => e.id === expense.id);
  if (index !== -1) {
    expenses[index] = expense;
  } else {
    expenses.push(expense);
  }
  expensesCache.set(userId, expenses);
};

export const updateIncomeCache = (userId: string, income: Income) => {
  const incomes = incomeCache.get(userId) || [];
  const index = incomes.findIndex(i => i.id === income.id);
  if (index !== -1) {
    incomes[index] = income;
  } else {
    incomes.push(income);
  }
  incomeCache.set(userId, incomes);
};

export const batchUpdateExpenses = async (user: User, expenses: Expense[]) => {
  const batch = writeBatch(db);
  expenses.forEach(expense => {
    const expenseRef = doc(db, "expenses", expense.id);
    const { id, ...expenseData } = expense;
    batch.update(expenseRef, expenseData);
    updateExpenseCache(user.uid, expense);
  });
  await batch.commit();
};

export const batchUpdateIncomes = async (user: User, incomes: Income[]) => {
  const batch = writeBatch(db);
  incomes.forEach(income => {
    const incomeRef = doc(db, INCOME_COLLECTION, income.id);
    const { id, ...incomeData } = income;
    batch.update(incomeRef, incomeData);
    updateIncomeCache(user.uid, income);
  });
  await batch.commit();
};

// Bank Account Operations

// Helper to remove undefined keys
const removeUndefined = (obj: any) => {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined));
};

export const addBankAccount = async (account: Omit<BankAccount, 'id'>, userId: string): Promise<BankAccount> => {
  try {
    const accountWithTimestamp = {
      ...account,
      userId,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      order: (account as any).order ?? 0,
    };

    // Sanitize to remove any potential undefined values
    const sanitizedAccount = removeUndefined(accountWithTimestamp);

    const docRef = await addDoc(collection(db, "bankAccounts"), sanitizedAccount);
    return { ...accountWithTimestamp, id: docRef.id } as BankAccount;
  } catch (error) {
    console.error("Error adding bank account:", error);
    throw new Error("Failed to add bank account");
  }
};

export const getBankAccounts = async (userId: string): Promise<BankAccount[]> => {
  try {
    const q = query(
      collection(db, "bankAccounts"),
      where("userId", "==", userId)
    );
    const querySnapshot = await getDocs(q);
    const accounts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BankAccount));
    // Sort by order locally (or use orderBy in firestore if index exists)
    return accounts.sort((a, b) => (a.order || 0) - (b.order || 0));
  } catch (error) {
    console.error("Error fetching bank accounts:", error);
    throw new Error("Failed to fetch bank accounts");
  }
};

export const updateBankAccount = async (account: BankAccount): Promise<void> => {
  try {
    const { id, ...updateData } = account;
    const accountRef = doc(db, "bankAccounts", id);

    const dataToUpdate = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    // Sanitize
    const sanitizedData = removeUndefined(dataToUpdate);

    await updateDoc(accountRef, sanitizedData as any);
  } catch (error) {
    console.error("Error updating bank account:", error);
    throw new Error("Failed to update bank account");
  }
};

export const deleteBankAccount = async (id: string, userId: string): Promise<void> => {
  try {
    const accountRef = doc(db, "bankAccounts", id);
    const accountDoc = await getDoc(accountRef);
    if (!accountDoc.exists() || accountDoc.data().userId !== userId) {
      throw new Error("You don't have permission to delete this bank account.");
    }
    await deleteDoc(accountRef);
  } catch (error) {
    console.error("Error deleting bank account:", error);
    throw new Error("Failed to delete bank account");
  }
};